/**
 * TierBadge — shows the org's current plan as a chip. Clicking it goes to the
 * Plan & Billing page, so the badge doubles as a one-click upgrade entry point
 * (per the "tier surfaces are conversion levers" principle).
 *
 * Reads the plan from the shared PlanContext (usePlan). Renders nothing until
 * the plan loads, so it never flashes a wrong tier.
 */

import { NavLink } from 'react-router-dom';
import { usePlan } from '../lib/usePlan';
import { tierMeta } from '../lib/planMeta';

interface TierBadgeProps {
  /** When true (default), the badge links to /admin/billing. */
  linkToBilling?: boolean;
  className?: string;
}

export default function TierBadge({ linkToBilling = true, className = '' }: TierBadgeProps) {
  const { plan, loading } = usePlan();
  if (loading || !plan) return null;

  const meta = tierMeta(plan.plan);
  const chip = (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold tracking-wide ${meta.badgeClasses} ${className}`}
      data-testid="tier-badge"
    >
      {meta.label}
    </span>
  );

  if (!linkToBilling) return chip;

  return (
    <NavLink to="/admin/billing" className="inline-flex hover:opacity-80 transition-opacity" title="View plan & billing">
      {chip}
    </NavLink>
  );
}
